/**
 * The screen a support call starts on.
 *
 * Standalone there is no host operations team with a dashboard, so the shell has to be
 * able to answer "what is this deployment actually talking to" without anyone opening a
 * terminal. Everything here is either configuration this build was given, or something the
 * service said about itself.
 *
 * The token panel shows **claims, never the token**. A screenshot of a support screen ends
 * up in a ticket system; a bearer token in one is a credential in a ticket system.
 *
 * It also states the two D-12 caveats plainly, because both will look like bugs the first
 * time they are met: the tenant claim name is a docstring assertion rather than observed
 * output, and the audience `inai-inventory` is not yet registered with the issuer, so the
 * first real token will be refused for having the wrong `aud` — correctly.
 */

import { Alert, Box, Chip, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { CONTRACT_VERSION, SERVER_SLICES, UNCOVERED_AREAS } from '@inai/inventory-core';
import { useHealth } from '@inai/inventory-core/react';
import { KeyValue, ScreenHeader, SectionCard } from '@inai/inventory-mui';

import { useAuth } from '../auth/context';
import { audienceList } from '../auth/token';
import { callbackUrl } from '../auth/redirect';
import { SHELL_CONFIG } from '../config';

function Mono({ children }: { children: ReactNode }) {
  return (
    <Box
      component="span"
      sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12.5 }}
    >
      {children}
    </Box>
  );
}

function expiryText(exp: unknown): string {
  if (typeof exp !== 'number') return 'no exp claim';
  const when = new Date(exp * 1000);
  const seconds = Math.round((when.getTime() - Date.now()) / 1000);
  const relative =
    seconds <= 0
      ? 'expired'
      : seconds < 3600
        ? `in ${Math.round(seconds / 60)} min`
        : `in ${(seconds / 3600).toFixed(1)} h`;
  return `${when.toISOString()} (${relative})`;
}

export function DiagnosticsScreen() {
  const { claims, tenantClaim, status } = useAuth();
  const health = useHealth();

  const audiences = audienceList(claims);
  const audienceMismatch =
    SHELL_CONFIG.audience !== null &&
    audiences.length > 0 &&
    !audiences.includes(SHELL_CONFIG.audience);

  return (
    <Box>
      <ScreenHeader
        title="Diagnostics"
        subtitle="What this shell was configured with, and what the service says about itself."
      />

      <Stack spacing={2}>
        <SectionCard title="Service">
          <Box sx={{ px: 2, py: 1 }}>
            <KeyValue label="API base URL">
              <Mono>{SHELL_CONFIG.apiBaseUrl}</Mono>
            </KeyValue>
            <KeyValue label="Health">
              {health.isLoading ? (
                'checking…'
              ) : health.error ? (
                <Chip size="small" color="error" variant="outlined" label="unreachable" />
              ) : (
                <Chip
                  size="small"
                  color={health.data?.status === 'ok' ? 'success' : 'warning'}
                  variant="outlined"
                  label={health.data?.status ?? 'unknown'}
                />
              )}
            </KeyValue>
            <KeyValue label="Storage mode">
              <Mono>{health.data?.mode ?? '—'}</Mono>
            </KeyValue>
            <KeyValue label="Store reachable">
              {health.data ? String(health.data.store.reachable) : '—'}
            </KeyValue>
            <KeyValue label="Store detail">
              <Typography sx={{ fontSize: 12.5 }}>{health.data?.store.detail ?? '—'}</Typography>
            </KeyValue>
          </Box>
        </SectionCard>

        <SectionCard title="Authentication" hint="D-12">
          <Box sx={{ px: 2, py: 1 }}>
            <KeyValue label="Mode">
              <Mono>{SHELL_CONFIG.authMode}</Mono>
            </KeyValue>
            <KeyValue label="Issuer">
              <Mono>{SHELL_CONFIG.authMode === 'manual' ? 'none' : SHELL_CONFIG.authUrl}</Mono>
            </KeyValue>
            <KeyValue label="Client id">
              <Mono>{SHELL_CONFIG.clientId}</Mono>
            </KeyValue>
            <KeyValue label="Redirect URI">
              <Mono>{SHELL_CONFIG.authMode === 'manual' ? 'none' : callbackUrl()}</Mono>
            </KeyValue>
            <KeyValue label="Audience requested">
              <Mono>{SHELL_CONFIG.audience ?? 'not configured'}</Mono>
            </KeyValue>
            <KeyValue label="Session status">
              <Mono>{status}</Mono>
            </KeyValue>
          </Box>
        </SectionCard>

        <SectionCard title="Token" hint="claims only — the token itself is never shown">
          <Box sx={{ px: 2, py: 1 }}>
            <KeyValue label="Subject">
              <Mono>{claims?.sub ?? '—'}</Mono>
            </KeyValue>
            <KeyValue label="Issuer claim">
              <Mono>{claims?.iss ?? '—'}</Mono>
            </KeyValue>
            <KeyValue label="Audience claim">
              <Mono>{audiences.length > 0 ? audiences.join(', ') : '—'}</Mono>
            </KeyValue>
            <KeyValue label="Expires">
              <Mono>{expiryText(claims?.exp)}</Mono>
            </KeyValue>
            <KeyValue label="Tenant claim read">
              <Mono>
                {tenantClaim.name ? `${tenantClaim.name} = ${tenantClaim.value}` : 'none present'}
              </Mono>
            </KeyValue>
            <KeyValue label="Recognised spellings present">
              <Mono>
                {tenantClaim.present.length > 0
                  ? tenantClaim.present.map((entry) => entry.name).join(', ')
                  : 'none of org, org_id, tenant, tenant_id'}
              </Mono>
            </KeyValue>
          </Box>
        </SectionCard>

        {tenantClaim.present.length > 1 ? (
          <Alert severity="warning" variant="outlined">
            <Typography sx={{ fontSize: 13 }}>
              This token carries more than one recognised tenant spelling. The service takes
              the first <em>present</em> one — <Mono>{tenantClaim.name}</Mono> — and does not
              fall through to the others. If they disagree, that is a token nobody
              understands and it should be raised with the issuer rather than worked around.
            </Typography>
          </Alert>
        ) : null}

        {audienceMismatch ? (
          <Alert severity="error" variant="outlined">
            <Typography sx={{ fontSize: 13 }}>
              This token&apos;s audience ({audiences.join(', ')}) does not include{' '}
              <Mono>{SHELL_CONFIG.audience}</Mono>. The service validates <Mono>aud</Mono>{' '}
              against its own <Mono>INVENTORY_AUTH_AUDIENCE</Mono> and will refuse it with a
              coded 401.
            </Typography>
          </Alert>
        ) : null}

        <Alert severity="info" variant="outlined">
          <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.5 }}>
            Two things that will look like bugs and are not (D-12)
          </Typography>
          <Typography sx={{ fontSize: 13 }} component="div">
            <Box component="ul" sx={{ m: 0, pl: 3 }}>
              <li>
                <strong>The audience is not registered yet.</strong> The proposed audience{' '}
                <Mono>inai-inventory</Mono> has to be configured at auth.xpertte.com before a
                token can carry it. Until then the first real token fails <Mono>aud</Mono>{' '}
                validation — correctly.
              </li>
              <li>
                <strong>The tenant claim name is not confirmed.</strong> ICRM documents{' '}
                <Mono>org</Mono> and its backend walks the same four spellings this service
                does, but no repository in the estate holds a real token or a JWKS snapshot,
                so <Mono>org</Mono> is a docstring assertion rather than observed output.
                Confirm it with the issuer&apos;s owner.
              </li>
            </Box>
          </Typography>
        </Alert>

        <SectionCard title="Client contract">
          <Box sx={{ px: 2, py: 1 }}>
            <KeyValue label="Contract version">
              <Mono>{CONTRACT_VERSION}</Mono>
            </KeyValue>
            <KeyValue label="Slices covered">
              <Mono>{SERVER_SLICES.join(', ')}</Mono>
            </KeyValue>
            <KeyValue label="Server areas with no client yet">
              <Mono>{UNCOVERED_AREAS.join(', ')}</Mono>
            </KeyValue>
          </Box>
        </SectionCard>
      </Stack>
    </Box>
  );
}
